import { Box, Theme } from "@outschool/backpack";
import { EnrollmentWarnings_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { Checkbox, Input } from "@outschool/ui-legacy-component-library";
import React, { useEffect, useRef, useState } from "react";

import * as Activity from "../../../../../shared/Activity";
import { ActivityEnrollQueryQuery } from "../../../../../shared/generated/graphql";
import { DateOfBirthPicker } from "../../../learner/DateOfBirthPicker";
import { LearnerNameWarning } from "../../../learner/LearnerNameWarning";
import {
  EnrollmentWarnings,
  StrictAgeRangeWarning,
  ToddlerNotAllowedWarning
} from "./EnrollmentWarnings";
import { LearnerItemType } from "./utils";

type LearnerEditableItemProps = {
  learnerItem: LearnerItemType;
  disabled: boolean;
  highlightErrors: boolean;
  className?: string;
  ignoreStrictAgeRange: boolean;
  activity: Pick<
    NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"],
    "uid" | "age_max" | "age_min" | "strictAgeRange"
  > &
    EnrollmentWarnings_ActivityFragment;
  onUpdate?(learnerItem: LearnerItemType): void;
  requestAgeRangeException?: (arg0: LearnerItemType) => void;
};

export enum DataTestIds {
  LearnerEditableItem = "LearnerEditableItem"
}

export default function LearnerEditableItem({
  learnerItem,
  disabled,
  highlightErrors,
  activity,
  ignoreStrictAgeRange,
  requestAgeRangeException,
  onUpdate = () => {}
}: LearnerEditableItemProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\LearnerEditableItem"
  );
  const nameFieldRef = useRef<HTMLInputElement>();

  const [showStrictAgeWarning, setShowStrictAgeWarning] = useState(false);
  const [showToddlerWarning, setShowToddlerWarning] = useState(false);

  const finalDisabled = showStrictAgeWarning || disabled;

  useEffect(() => {
    if (nameFieldRef.current) {
      nameFieldRef.current.focus();
    }
  }, []);
  const { learner, checked } = learnerItem;

  const [pickedBirthYear, setPickedBirthYear] = useState(
    dayjs(learner.date_of_birth).year()
  );
  const [pickedBirthMonth, setPickedBirthMonth] = useState(
    dayjs(learner.date_of_birth).month() + 1
  );

  useEffect(() => {
    if (learner.date_of_birth) {
      setPickedBirthYear(dayjs(learner.date_of_birth).year());
      setPickedBirthMonth(dayjs(learner.date_of_birth).month());
    }
  }, [learner.date_of_birth]);

  function handleNameChange(name: string) {
    onUpdate({
      ...learnerItem,
      learner: {
        ...learnerItem.learner,
        name
      }
    });
  }

  const handleAgeChange = (
    birthMonth: number,
    birthYear: number,
    age: number
  ) => {
    if (!birthMonth || !birthYear) {
      return;
    } else if (birthMonth && birthYear) {
      setPickedBirthYear(birthYear);
      setPickedBirthMonth(birthMonth);
      // // Calculate DoB using UTC to avoid timezone issues
      const dateOfBirth = dayjs.utc(`${birthYear}-${birthMonth}-1`, "YYYY-M-D");
      let checked = learnerItem.checked;
      if (
        Activity.learnerOutsideStrictAgeRange(
          activity,
          ignoreStrictAgeRange,
          age
        )
      ) {
        setShowStrictAgeWarning(true);
        checked = false;
      } else if (Activity.toddlerIsNotAllowed(activity, age)) {
        setShowToddlerWarning(true);
        checked = false;
      } else {
        setShowStrictAgeWarning(false);
        setShowToddlerWarning(false);
      }
      onUpdate({
        ...learnerItem,
        checked,
        learner: {
          ...learnerItem.learner,
          age,
          date_of_birth: dateOfBirth
        }
      });
    }
  };

  function handleCheckedChange(checked: boolean) {
    onUpdate({ ...learnerItem, checked });
  }

  const hasErrors =
    highlightErrors &&
    (!learner.name || !learner.name.trim() || !learner.age) &&
    checked;

  const defaultYear =
    dayjs().year() -
    Math.round(((activity.age_max ?? 18) + (activity.age_min ?? 3)) / 2.0);

  return (
    <Box
      data-test-id={DataTestIds.LearnerEditableItem}
      sx={{
        marginBottom: "0.5em"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginRight: "0.5em"
          }}
        >
          <Checkbox
            sx={{ marginRight: "0.5em" }}
            checked={checked}
            disabled={finalDisabled}
            onChange={e => handleCheckedChange(e.currentTarget.checked)}
          />
          <Input
            className="amp-block"
            type="text"
            ref={nameFieldRef}
            value={learner.name || ""}
            data-private
            disabled={disabled}
            placeholder={t`First name`}
            sx={(theme: Theme) => ({
              width: "100%",
              [theme.breakpoints.down("sm")]: {
                width: "239px"
              }
            })}
            onChange={e => handleNameChange(e.currentTarget.value)}
          />
        </Box>
        <Box
          sx={(theme: Theme) => ({
            width: ["100%", "auto"],
            paddingTop: "7px",
            [theme.breakpoints.down("sm")]: {
              marginLeft: "38px",
              paddingTop: "15px"
            }
          })}
        >
          <DateOfBirthPicker
            birthMonth={pickedBirthMonth}
            birthYear={pickedBirthYear}
            onChange={handleAgeChange}
            defaultYear={defaultYear}
          />
        </Box>
      </Box>
      <LearnerNameWarning name={nameFieldRef.current?.value || ""} />
      {hasErrors && (
        <Box
          sx={{
            marginLeft: "2em",
            color: "error.500"
          }}
        >
          {t`Please enter name and date of birth`}
        </Box>
      )}
      {checked && (
        <EnrollmentWarnings
          ignoreStrictAgeRange={ignoreStrictAgeRange}
          learner={learner}
          activity={activity}
        />
      )}
      {showStrictAgeWarning && (
        <StrictAgeRangeWarning
          activity={activity}
          ignoreStrictAgeRange={ignoreStrictAgeRange}
          learner={learner}
          requestException={requestAgeRangeException!}
        />
      )}
      {showToddlerWarning && (
        <ToddlerNotAllowedWarning activity={activity} learner={learner} />
      )}
    </Box>
  );
}
